import { fmtEUR, fmtDate, fmtNum } from "@/lib/api";
import { shortConcept } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2 } from "lucide-react";
import { RECON_BADGE } from "./shared";

export function TransactionsCard({ data, filters, accounts, page, setPage, onFilter, accKind, onEdit, onDelete }) {
  const pages = Math.ceil((data.total || 0) / 25);

  return (
    <Card data-testid="transactions-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="text-base font-semibold font-heading">Movimientos ({fmtNum(data.total)})</CardTitle>
        <div className="flex gap-2 flex-wrap">
          <Select value={filters.account_id} onValueChange={(v) => onFilter({ account_id: v })}>
            <SelectTrigger data-testid="tx-account-filter" className="w-48 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las cuentas</SelectItem>
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.recon} onValueChange={(v) => onFilter({ recon: v })}>
            <SelectTrigger data-testid="tx-recon-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendiente" data-testid="recon-opt-pendiente">Sin conciliar</SelectItem>
              <SelectItem value="conciliado" data-testid="recon-opt-conciliado">Conciliados</SelectItem>
              <SelectItem value="categorizado" data-testid="recon-opt-categorizado">Categorizados</SelectItem>
              <SelectItem value="ignorado" data-testid="recon-opt-ignorado">Ignorados</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="tx-search" placeholder="Buscar concepto/ref..." value={filters.q}
            onChange={(e) => onFilter({ q: e.target.value })} className="w-52 h-9" />
          <Input data-testid="tx-date-from" type="date" value={filters.date_from}
            onChange={(e) => onFilter({ date_from: e.target.value })} className="w-36 h-9" />
          <Input data-testid="tx-date-to" type="date" value={filters.date_to}
            onChange={(e) => onFilter({ date_to: e.target.value })} className="w-36 h-9" />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Concepto</TableHead>
              <TableHead>Cuenta</TableHead>
              <TableHead>Referencia</TableHead>
              <TableHead className="text-right">Importe</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead>Conciliación</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.transactions.map((t) => (
              <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
                <TableCell className="text-sm">{fmtDate(t.date)}</TableCell>
                <TableCell className="text-sm max-w-72">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate" title={t.concept}>{shortConcept(t.concept)}</span>
                    {t.confidence === "revisar" && (
                      <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-[10px] shrink-0">Revisar</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{t.account_name}</TableCell>
                <TableCell className="font-num text-xs text-slate-500">{t.reference || "—"}</TableCell>
                <TableCell className={`text-right font-num font-semibold ${t.amount >= 0 ? "text-green-700" : "text-red-700"}`}>
                  {fmtEUR(t.amount)}
                </TableCell>
                <TableCell className="text-right font-num text-slate-500">{t.balance !== null && t.balance !== undefined ? fmtEUR(t.balance) : "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" data-testid={`tx-recon-badge-${t.id}`}
                    className={(RECON_BADGE[t.reconciliation_status] || RECON_BADGE.pendiente).cls}>
                    {(RECON_BADGE[t.reconciliation_status] || RECON_BADGE.pendiente).label}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    {(t.reconciliation_status === "pendiente" || t.reconciliation_status === "ignorado") && (
                      <Button data-testid={`tx-edit-${t.id}`} size="sm" variant="outline" className="gap-1" onClick={() => onEdit(t)}>
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </Button>
                    )}
                    {accKind(t.account_id) === "efectivo" && (t.reconciliation_status === "pendiente" || t.reconciliation_status === "ignorado") && (
                      <Button data-testid={`tx-delete-${t.id}`} size="sm" variant="outline"
                        className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => onDelete(t)}>
                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {data.transactions.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400 text-sm">
                Sin movimientos. Importa un extracto para empezar.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <Button data-testid="tx-prev-page" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
            <span className="text-xs text-slate-500">Página {page} de {pages}</span>
            <Button data-testid="tx-next-page" variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
